/**
 * Language Selector Component
 * Allows users to switch between available languages
 */

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Globe
} from '@phosphor-icons/react';

export function LanguageSelector({ variant = 'dropdown', className = '' }) {
  const { language, setLanguage, languages } = useLanguage();

  if (variant === 'buttons') {
    return (
      <div className={`flex gap-1 ${className}`}>
        {Object.values(languages).map((lang) => (
          <button
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`px-2 py-1 text-xs rounded transition-colors ${
              language === lang.code
                ? 'bg-emerald-500 text-v4-on-accent'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
            title={lang.name}
          >
            {lang.flag} {lang.code.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  // Dropdown variant (default)
  return (
    <div className={`relative inline-block ${className}`}>
      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
        className="appearance-none bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg pl-8 pr-4 py-2 focus:ring-emerald-500 focus:border-emerald-500 cursor-pointer hover:bg-slate-700 transition-colors"
      >
        {Object.values(languages).map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.name}
          </option>
        ))}
      </select>
      <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
    </div>
  );
}

/**
 * Compact language toggle for navbar
 */
export function LanguageToggle({ className = '' }) {
  const { language, setLanguage, languages } = useLanguage();

  const toggleLanguage = () => {
    const codes = Object.keys(languages);
    const currentIndex = codes.indexOf(language);
    const nextIndex = (currentIndex + 1) % codes.length;
    setLanguage(codes[nextIndex]);
  };

  return (
    <button
      onClick={toggleLanguage}
      className={`flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-sm text-slate-200 transition-colors ${className}`}
      title={`Current: ${languages[language]?.name}. Click to change.`}
    >
      <Globe className="h-4 w-4 text-slate-400" />
      <span className="text-xs text-slate-400">{language.toUpperCase()}</span>
    </button>
  );
}

export default LanguageSelector;
